/**
 * Meta Pixel client helper.
 *
 * Fires a pixel event in the browser via `fbq` and mirrors it through the
 * Conversions API server function with the same `event_id` so Meta can
 * deduplicate the pair.
 */

import { sendMetaConversionEvent, type MetaEventCustomData, type MetaEventUserData } from "@/lib/meta-pixel.server";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function generateEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Meta's official standard events.
 * Reference: https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 *
 * Only these names trigger `fbq('track', …)`. Anything else is a custom event
 * and must use `trackMetaCustom` (which calls `fbq('trackCustom', …)`).
 * Meta does NOT deduplicate `trackCustom` events against CAPI identically —
 * custom events should still carry an `event_id` but they are reported
 * separately in Events Manager.
 */
export type MetaStandardEvent =
  | "AddPaymentInfo"
  | "AddToCart"
  | "AddToWishlist"
  | "CompleteRegistration"
  | "Contact"
  | "CustomizeProduct"
  | "Donate"
  | "FindLocation"
  | "InitiateCheckout"
  | "Lead"
  | "PageView"
  | "Purchase"
  | "Schedule"
  | "Search"
  | "StartTrial"
  | "SubmitApplication"
  | "Subscribe"
  | "ViewContent";

export type TrackMetaOptions = {
  customData?: MetaEventCustomData;
  userData?: MetaEventUserData;
  eventId?: string;
};

const ALLOWED_META_TRACKING_HOSTS = new Set(["ai.zapify.pro", "zapify.pro"]);

function isAllowedMetaTrackingHost(hostname: string): boolean {
  return ALLOWED_META_TRACKING_HOSTS.has(hostname);
}

async function dispatchMetaEvent(
  fbqAction: "track" | "trackCustom",
  eventName: string,
  options: TrackMetaOptions,
) {
  if (typeof window === "undefined") return;
  if (!isAllowedMetaTrackingHost(window.location.hostname)) return;

  const eventId = options.eventId ?? generateEventId();
  const eventSourceUrl = window.location.href;

  if (window.fbq) {
    window.fbq(fbqAction, eventName, options.customData ?? {}, { eventID: eventId });
  }

  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");

  try {
    await sendMetaConversionEvent({
      data: {
        eventName,
        eventId,
        eventSourceUrl,
        userData: {
          ...options.userData,
          fbp: options.userData?.fbp ?? fbp,
          fbc: options.userData?.fbc ?? fbc,
        },
        customData: options.customData,
      },
    });
  } catch (error) {
    console.error("Meta CAPI relay failed", error);
  }

  return eventId;
}

/**
 * Track a Meta *standard* event. The `eventName` type is restricted to the
 * official list so misspellings and non-standard names are caught at compile
 * time. Use `trackMetaCustom` for anything outside this list.
 */
export function trackMeta(eventName: MetaStandardEvent, options: TrackMetaOptions = {}) {
  return dispatchMetaEvent("track", eventName, options);
}

/**
 * Track a non-standard event. Sent with `fbq('trackCustom', …)` and mirrored
 * to the Conversions API under the same name.
 */
export function trackMetaCustom(eventName: string, options: TrackMetaOptions = {}) {
  return dispatchMetaEvent("trackCustom", eventName, options);
}

export function trackMetaPageView() {
  return trackMeta("PageView");
}
